import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { TrackingMap } from '@/backoffice/components/tracking/TrackingMap';
import { fetchLandingData } from '@/domains/landing-page/services/landing.api';
import { fetchPublicTracking } from '@/domains/landing-page/services/landing.api';
import type { LandingSampleTrackingCode, PublicTrackingData } from '@/domains/landing-page/types';
import { formatCurrency } from '@/backoffice/utils';
import { Loader2, MapPin, Package, Search, Truck } from 'lucide-react';

function toCoord(value: number | string | null | undefined): number | null {
  if (value == null) return null;
  const n = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(n) ? n : null;
}

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'in_progress':
      return 'bg-orange-100 text-orange-800';
    case 'delivered':
    case 'confirmed_livree':
      return 'bg-emerald-100 text-emerald-800';
    case 'cancelled':
    case 'rejected':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-blue-100 text-blue-800';
  }
}

export default function TrackingPage() {
  const [trackingCode, setTrackingCode] = useState('');
  const [deliveryData, setDeliveryData] = useState<PublicTrackingData | null>(null);
  const [sampleCodes, setSampleCodes] = useState<LandingSampleTrackingCode[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchLandingData()
      .then((data) => setSampleCodes(data.sample_tracking_codes))
      .catch(() => setSampleCodes([]));
  }, []);

  const handleTrackDelivery = async (code?: string) => {
    const value = (code ?? trackingCode).trim();
    if (!value) return;

    setIsLoading(true);
    setError(null);
    setDeliveryData(null);

    try {
      const data = await fetchPublicTracking(value);
      setDeliveryData(data);
      setTrackingCode(value);
    } catch {
      setError('Aucune commande trouvée pour ce code de suivi. Vérifiez le format CMD-123.');
    } finally {
      setIsLoading(false);
    }
  };

  const pickup =
    deliveryData?.order.departure_latitude != null && deliveryData?.order.departure_longitude != null
      ? {
          lat: toCoord(deliveryData.order.departure_latitude)!,
          lng: toCoord(deliveryData.order.departure_longitude)!,
          label: deliveryData.order.departure_zone?.name ?? 'Collecte',
        }
      : null;

  const dropoff =
    deliveryData?.order.arrival_latitude != null && deliveryData?.order.arrival_longitude != null
      ? {
          lat: toCoord(deliveryData.order.arrival_latitude)!,
          lng: toCoord(deliveryData.order.arrival_longitude)!,
          label: deliveryData.order.arrival_zone?.name ?? 'Livraison',
        }
      : null;

  const driver =
    deliveryData?.driver_location
      ? {
          lat: toCoord(deliveryData.driver_location.latitude)!,
          lng: toCoord(deliveryData.driver_location.longitude)!,
          label: 'Livreur',
        }
      : null;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-teal-600 rounded-lg flex items-center justify-center">
                <Truck className="w-4 h-4 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900" style={{ fontFamily: '"Pacifico", serif' }}>
                ONAPILA
              </span>
            </Link>

            <nav className="hidden md:flex space-x-8">
              <Link to="/" className="text-gray-600 hover:text-teal-600 font-medium">Accueil</Link>
              <Link to="/tracking" className="text-teal-600 font-medium">Suivi</Link>
              <Link to="/login" className="text-gray-600 hover:text-teal-600 font-medium">Connexion</Link>
            </nav>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Suivi de livraison</h1>
            <p className="text-gray-600">
              Entrez votre code de suivi (format <strong>CMD-123</strong>) pour voir l'évolution de votre commande
            </p>
          </div>

          <div className="max-w-md mx-auto">
            <div className="flex space-x-3">
              <input
                type="text"
                value={trackingCode}
                onChange={(e) => setTrackingCode(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleTrackDelivery()}
                placeholder="Ex: CMD-42"
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
              <button
                type="button"
                onClick={() => handleTrackDelivery()}
                disabled={isLoading || !trackingCode.trim()}
                className="px-6 py-3 bg-teal-600 hover:bg-teal-700 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors whitespace-nowrap flex items-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Recherche...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4" />
                    Suivre
                  </>
                )}
              </button>
            </div>
            {error && <p className="mt-3 text-sm text-red-600 text-center">{error}</p>}
          </div>
        </div>

        {deliveryData && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h2 className="text-xl font-bold text-gray-900">Commande {deliveryData.order.code}</h2>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusBadgeClass(deliveryData.order.status)}`}>
                  {deliveryData.order.status_label}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {deliveryData.driver && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-3">Votre livreur</h3>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center">
                        <Truck className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{deliveryData.driver.name}</p>
                        <p className="text-xs text-gray-500">{deliveryData.driver.type}</p>
                        <p className="text-sm text-gray-600">★ {deliveryData.driver.rating}</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600">
                      <Package className="w-4 h-4 inline mr-1" />
                      {deliveryData.driver.vehicle}
                    </p>
                  </div>
                )}

                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Détails</h3>
                  <div className="space-y-2 text-sm">
                    {deliveryData.order.total_delivery_fee != null && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Prix</span>
                        <span className="font-medium">{formatCurrency(deliveryData.order.total_delivery_fee)}</span>
                      </div>
                    )}
                    {deliveryData.eta_minutes != null && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Temps estimé</span>
                        <span className="font-medium">{deliveryData.eta_minutes} min</span>
                      </div>
                    )}
                    {deliveryData.distance_km != null && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Distance</span>
                        <span className="font-medium">{deliveryData.distance_km} km</span>
                      </div>
                    )}
                    {deliveryData.order.delivery_type && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Type</span>
                        <span className="font-medium">{deliveryData.order.delivery_type.name}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Adresses</h3>
                  <div className="space-y-3 text-sm">
                    <div>
                      <p className="text-gray-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> Collecte</p>
                      <p className="font-medium text-gray-900">{deliveryData.order.departure_address}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 flex items-center gap-1"><MapPin className="w-3 h-3 text-emerald-500" /> Livraison</p>
                      <p className="font-medium text-gray-900">{deliveryData.order.arrival_address}</p>
                    </div>
                  </div>
                </div>
              </div>

              {deliveryData.timeline.length > 0 && (
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <h3 className="font-semibold text-gray-900 mb-3">Historique</h3>
                  <div className="space-y-2">
                    {deliveryData.timeline.map((log) => (
                      <div key={log.id} className="text-sm text-gray-600 flex justify-between gap-4">
                        <span className="font-medium text-gray-800">{log.note ?? log.status}</span>
                        {log.created_at && (
                          <span className="text-xs text-gray-400 shrink-0">
                            {new Date(log.created_at).toLocaleString('fr-FR')}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl shadow-lg p-4 h-96">
              <TrackingMap pickup={pickup} dropoff={dropoff} driver={driver} className="h-full w-full rounded-lg" />
            </div>
          </div>
        )}

        {!deliveryData && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            {sampleCodes.length > 0 && (
              <>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Commandes récentes à suivre</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  {sampleCodes.map((item) => (
                    <button
                      key={item.code}
                      type="button"
                      onClick={() => handleTrackDelivery(item.code)}
                      className="p-4 border border-gray-200 rounded-lg hover:border-teal-500 hover:bg-teal-50 transition-colors text-left"
                    >
                      <div className="font-medium text-gray-900">{item.code}</div>
                      <div className="text-sm text-gray-600">{item.type} · {item.status_label}</div>
                    </button>
                  ))}
                </div>
              </>
            )}

            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">Comment obtenir votre code de suivi ?</h4>
              <div className="space-y-2 text-sm text-blue-800">
                <p>• <strong>Clients :</strong> Consultez votre <Link to="/dashboard/client/commandes" className="underline">tableau de bord</Link> après connexion</p>
                <p>• <strong>Livreurs :</strong> Le code est affiché sur chaque mission acceptée</p>
                <p>• <strong>Format :</strong> CMD- suivi de l'identifiant de commande (ex. CMD-15)</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
